import { Button, Icon, Paper, Typography } from '@/ui';

import {
  formatAppointmentDate,
  formatAppointmentTimeRange,
  hasRenderableAppointmentIdentity,
  isHttpsAppointmentUrl,
} from './AppointmentCard';

import type { DecodedBrowserAppointment } from './types';
import type { FC } from 'react';

interface MemberAppointmentRowProps {
  appointment: DecodedBrowserAppointment;
  onCancel?: (appointment: DecodedBrowserAppointment) => void;
  onReschedule?: (appointment: DecodedBrowserAppointment) => void;
}

// Card visual parity with MemberSessionCard: rounded Paper with the coach
// display name headline, date on top, time+timezone below. Actions render as
// explicit buttons rather than a kebab menu because native cancel/reschedule
// currently owns its own flow and there is no equivalent to the legacy
// "Provide Feedback" / "View Profile" / "Message" set for native.
const MemberAppointmentRow: FC<MemberAppointmentRowProps> = ({
  appointment,
  onCancel = undefined,
  onReschedule = undefined,
}) => {
  if (!hasRenderableAppointmentIdentity(appointment)) {
    return (
      <Paper
        className="flex min-h-[90px] w-full items-start justify-between gap-4 rounded-xl bg-white p-6"
        elevation={1}
      >
        <Typography variant="body1">Appointment unavailable</Typography>
      </Paper>
    );
  }

  const isCancelled =
    appointment.lifecycleStatus === 'cancelled' ||
    appointment.lifecycleStatus === 'cancellation_pending';
  const isDone = Date.parse(appointment.currentSchedule.endAt) < Date.now();
  const showJoin =
    appointment.actionCapabilities.join.allowed &&
    isHttpsAppointmentUrl(appointment.meeting.joinUrl) &&
    !isCancelled &&
    !isDone;
  const showReschedule =
    Boolean(onReschedule) &&
    appointment.actionCapabilities.reschedule.allowed &&
    appointment.actionCapabilities.reschedule.scopes.includes('appointment') &&
    !isCancelled &&
    !isDone;
  const showCancel =
    Boolean(onCancel) &&
    appointment.actionCapabilities.cancel.allowed &&
    appointment.actionCapabilities.cancel.scopes.includes('appointment') &&
    !isCancelled &&
    !isDone;

  const timeZone = appointment.currentSchedule.timezone;
  const dateHeadline = formatAppointmentDate(
    appointment.currentSchedule.startAt,
    timeZone,
  );
  const timeRange = formatAppointmentTimeRange(
    appointment.currentSchedule.startAt,
    appointment.currentSchedule.endAt,
    timeZone,
  );
  const coachName = appointment.expectedIdentities.coach.displayName;

  return (
    <Paper
      className="flex min-h-[90px] w-full flex-col items-stretch gap-4 rounded-xl bg-white p-6 md:flex-row md:items-center md:justify-between"
      elevation={1}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Typography className="truncate" variant="h5">
          {dateHeadline}
        </Typography>
        <Typography className="truncate text-cement-400" variant="body2">
          {coachName} &middot; {timeRange}
        </Typography>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {showJoin && appointment.meeting.joinUrl && (
          <Button
            href={appointment.meeting.joinUrl}
            rel="noreferrer noopener"
            startIcon={<Icon>videocam</Icon>}
            target="_blank"
            variant="contained"
          >
            Join session
          </Button>
        )}
        {showReschedule && (
          <Button
            variant="outlined"
            onClick={() => onReschedule?.(appointment)}
          >
            Reschedule
          </Button>
        )}
        {showCancel && (
          <Button variant="outlined" onClick={() => onCancel?.(appointment)}>
            Cancel
          </Button>
        )}
      </div>
    </Paper>
  );
};

export default MemberAppointmentRow;
